var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Inset, Pane, Scroll, Link, Badge } from '@criteria-labs/react';
import clsx from 'clsx';
import React from 'react';
const colors = {
    indigo: {
        current: 'bg-indigo-100 dark:bg-indigo-950'
    },
    blue: {
        current: 'bg-blue-100 dark:bg-blue-950'
    }
};
export const Sidebar = React.forwardRef(function Sidebar(_a, ref) {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (_jsx(Pane, Object.assign({ as: "nav", "aria-label": "Sidebar", ref: ref }, props, { children: _jsx(Scroll, { children: _jsx(Inset, { compact: true, children: children }) }) })));
});
export function SidebarItem(_a) {
    var { current, indent = 0, color, className } = _a, props = __rest(_a, ["current", "indent", "color", "className"]);
    const classes = clsx(className, 
    // Base styles
    'group rounded-md pr-1.5 py-1.5 focus:outline-none', 
    // Text styles
    'text-left text-base/5 sm:text-sm/5 forced-colors:text-[CanvasText]', current
        ? ['text-zinc-900 dark:text-white', colors[color !== null && color !== void 0 ? color : 'blue'].current]
        : ['text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'], 
    // Use subgrid when available but fallback to an explicit grid layout if not
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid', 
    // Icon
    '[&>[data-slot=icon]]:col-start-1 [&>[data-slot=icon]]:row-start-1 [&>[data-slot=icon]]:mr-2.5 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:mr-2 [&>[data-slot=icon]]:sm:size-4'
    // '[&>[data-slot=icon]]:text-zinc-500 [&>[data-slot=icon]]:focus:text-white [&>[data-slot=icon]]:dark:text-zinc-500 [&>[data-slot=icon]]:focus:dark:text-white'
    );
    return 'href' in props ? (_jsx(Link, Object.assign({ "aria-current": current ? 'page' : undefined, className: classes, style: { paddingLeft: `calc(${indent} * 1.5rem + 0.375rem)` } }, props))) : (_jsx("button", Object.assign({ type: "button", className: classes, style: { paddingLeft: `calc(${indent} * 1.5rem + 0.375rem)` } }, props)));
}
export function SidebarHeader(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return _jsx("div", Object.assign({}, props, { className: clsx(className, 'col-span-5') }));
}
export function SidebarSection(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("div", Object.assign({}, props, { className: clsx(className, 
        // Define grid at the section level instead of the item level if subgrid is supported
        'col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]') })));
}
export function SidebarHeading(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("header", Object.assign({}, props, { className: clsx(className, 'col-span-full grid grid-cols-[1fr,auto] gap-x-12 px-1.5 pb-1 pt-4 text-sm/5 font-medium text-zinc-500 sm:text-xs/5 dark:text-zinc-400') })));
}
export function SidebarLabel(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return _jsx("div", Object.assign({}, props, { "data-slot": "label", className: clsx(className, 'col-start-1 row-start-1') }, props));
}
export function SidebarBadge(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(Badge, Object.assign({ "data-slot": "badge", className: clsx(className, 'col-start-5 row-start-1 flex justify-self-end') }, props)));
}
export function SidebarLayout({ sidebar, className, children }) {
    return (_jsxs("div", { className: clsx([
            className,
            // Basic layout
            'relative h-full w-full',
            // Background color
            'bg-white',
            // Dark mode
            'dark:bg-zinc-950'
        ]), children: [_jsx("div", { className: clsx([
                    'absolute inset-y-0 hidden lg:block',
                    // Pane width
                    'lg:w-64',
                    // Border
                    'border-r border-zinc-950/10 dark:border-white/10'
                ]), children: sidebar }), _jsx("div", { className: "h-full lg:pl-64", children: children })] }));
}
